import {
  PickupQuoteRequest,
  PickupQuoteResponse,
  PickupQuoteSnapshotResponse,
  CheckoutStartRequest,
  CheckoutConfirmRequest,
  CheckoutStartResponse,
  CheckoutStatusResponse,
  CustomerReturnsResponse,
  ErrorResponse,
  SupportReturnsResponse,
  SupportMessageRequest,
  SupportMessageResponse,
} from './types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8080/api';

export const SAFE_REQUEST_ERROR_MESSAGE = "We couldn't complete that request. Please try again.";

export class ApiError extends Error {
  status: number;

  payload?: ErrorResponse;

  constructor(message: string, status: number, payload?: ErrorResponse) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

interface ApiRequestInit extends RequestInit {
  accessToken?: string;
}

async function request<T>(path: string, init?: ApiRequestInit): Promise<T> {
  const { accessToken, headers, ...requestInit } = init ?? {};
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...requestInit,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
    });
  } catch {
    throw new ApiError(SAFE_REQUEST_ERROR_MESSAGE, 0);
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  let body: unknown = null;

  if (isJson) {
    try {
      body = await response.json();
    } catch {
      throw new ApiError(SAFE_REQUEST_ERROR_MESSAGE, response.status);
    }
  }

  if (!response.ok) {
    const payload = body as ErrorResponse | null;
    const hasCustomerAdvice =
      response.status < 500 && typeof payload?.message === 'string' && payload.message.trim().length > 0;
    const message = hasCustomerAdvice ? payload.message.trim() : SAFE_REQUEST_ERROR_MESSAGE;
    throw new ApiError(message, response.status, payload ?? undefined);
  }

  return body as T;
}

export function getPickupQuote(payload: PickupQuoteRequest): Promise<PickupQuoteResponse> {
  return request<PickupQuoteResponse>('/v1/pickups/quote', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getPickupQuoteById(quoteId: string): Promise<PickupQuoteSnapshotResponse> {
  return request<PickupQuoteSnapshotResponse>(`/v1/pickups/quote/${encodeURIComponent(quoteId)}`, {
    cache: 'no-store',
  });
}

export function startCheckout(payload: CheckoutStartRequest): Promise<CheckoutStartResponse> {
  return request<CheckoutStartResponse>('/v1/checkout/start', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function confirmCheckout(payload: CheckoutConfirmRequest): Promise<CheckoutStartResponse> {
  return request<CheckoutStartResponse>('/v1/checkout/confirm', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getCheckoutStatus(returnId: string): Promise<CheckoutStatusResponse> {
  return request<CheckoutStatusResponse>(`/v1/checkout/${encodeURIComponent(returnId)}`, {
    cache: 'no-store',
  });
}

export async function getCustomerReturns(getAccessToken: () => Promise<string>): Promise<CustomerReturnsResponse> {
  const accessToken = await getAccessToken();
  return request<CustomerReturnsResponse>('/v1/account/returns', {
    accessToken,
  });
}

export async function getSupportReturns(
  getAccessToken: () => Promise<string>,
  limit = 50,
): Promise<SupportReturnsResponse> {
  const accessToken = await getAccessToken();
  const normalizedLimit = Math.max(1, Math.min(100, Math.floor(limit)));
  return request<SupportReturnsResponse>(`/v1/support/returns?limit=${normalizedLimit}`, {
    accessToken,
  });
}

export async function getCustomerReturn(
  returnId: string,
  getAccessToken: () => Promise<string>,
): Promise<CheckoutStatusResponse> {
  const accessToken = await getAccessToken();
  return request<CheckoutStatusResponse>(`/v1/account/returns/${returnId}`, {
    accessToken,
  });
}

export function getReturnWithManagementToken(
  returnId: string,
  managementToken: string,
): Promise<CheckoutStatusResponse> {
  return request<CheckoutStatusResponse>(`/v1/returns/${encodeURIComponent(returnId)}`, {
    headers: {
      'X-Return-Management-Token': managementToken,
    },
  });
}

export async function getSupportReturn(
  returnId: string,
  getAccessToken: () => Promise<string>,
): Promise<CheckoutStatusResponse> {
  const accessToken = await getAccessToken();
  return request<CheckoutStatusResponse>(`/v1/support/returns/${returnId}`, {
    accessToken,
  });
}

export function sendSupportMessage(payload: SupportMessageRequest): Promise<SupportMessageResponse> {
  return request<SupportMessageResponse>('/v1/support/messages', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function cancelCustomerReturnPickup(
  returnId: string,
  getAccessToken: () => Promise<string>,
): Promise<CheckoutStatusResponse> {
  const accessToken = await getAccessToken();
  return request<CheckoutStatusResponse>(`/v1/account/returns/${returnId}/pickup`, {
    method: 'DELETE',
    accessToken,
  });
}

export function cancelReturnPickupWithManagementToken(
  returnId: string,
  managementToken: string,
): Promise<CheckoutStatusResponse> {
  return request<CheckoutStatusResponse>(`/v1/returns/${encodeURIComponent(returnId)}/pickup`, {
    method: 'DELETE',
    headers: {
      'X-Return-Management-Token': managementToken,
    },
  });
}
